/*
 * Design: Swiss Precision × 和の余白
 * 81合同会社の会社概要・経営理念・代表メッセージ
 * Mobile: 余白・フォントサイズ最適化、カード間隔調整
 */

import { motion } from "framer-motion";
import { Heart, Target, Eye, Handshake, ExternalLink } from "lucide-react";

const VALUES = [
  {
    icon: Heart,
    title: "寄り添い、喜びを分かち合う",
    description: "お客様の成功が、私たちの喜び。現場に寄り添い、共に成果を分かち合うパートナーであり続けます。",
  },
  {
    icon: Target,
    title: "厳しさと思いやりを持って、事業の成長を願う",
    description: "本質的な課題に向き合い、時に厳しい提言も行いながら、お客様の事業成長を本気で願います。",
  },
  {
    icon: Eye,
    title: "改善と成長を促す",
    description: "一度の実装で終わらせず、継続的な改善サイクルを回し、組織全体の成長を促進します。",
  },
  {
    icon: Handshake,
    title: "右腕となる働きをする",
    description: "経営者の右腕として、戦略から実行まで。外部パートナーでありながら、社内チームのように動きます。",
  },
];

const COMPANY_INFO = [
  { label: "会社名", value: "81合同会社（eightone LLC）" },
  { label: "代表", value: "代表社員 藤井 壮太郎" },
  { label: "設立", value: "2019年12月25日" },
  { label: "所在地", value: "大阪市中央区平野町2-3-7 アーバンエース北浜ビル1F" },
  { label: "事業内容", value: "DX伴走支援・研修・デジタルインフラ支援" },
  { label: "対応エリア", value: "広島・大阪・東京を拠点に全国対応" },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function CompanySection() {
  return (
    <section id="company" className="py-14 sm:py-20 lg:py-28 bg-white">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-16">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              About Us
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            81合同会社について
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-3xl leading-relaxed">
            「おもろい」にチャレンジし、社会にインパクトを与える。
            現場・人・仕組みを一緒に育てる伴走型DXパートナーです。
          </p>
        </motion.div>

        {/* Mission / Vision / Representative Message */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8 lg:gap-12 mb-10 sm:mb-16">
          {/* Left: Representative Message */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-5 sm:p-8 lg:p-10 h-full">
              <span
                className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666] mb-3 sm:mb-4 block"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Our Philosophy
              </span>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A] mb-4 sm:mb-6 leading-snug">
                DXは「導入」ではなく「育てる」もの。
              </h3>
              <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-[#333333] leading-relaxed">
                <p>
                  私たちは、テクノロジーの導入そのものを目的としません。
                  現場の皆さまと一緒に、小さな一歩から始めて、試行錯誤しながら成長していく——
                  その過程こそがDXの本質だと考えています。
                </p>
                <p>
                  「IT導入」で終わらせず、現場の業務フローを起点に、KPI設計・運用設計・内製化支援まで踏み込むことで、
                  <span className="font-bold text-[#E03030]">定着・生産性向上・見える化</span>を実現します。
                </p>
                <p>
                  目的は「導入」ではなく「成果」。チームで良い仕事をし、家族と仲間を幸せに——
                  この価値観のもと、地域と企業の持続的成長を共創します。
                </p>
              </div>
              <div className="mt-5 sm:mt-6 pt-4 sm:pt-6 border-t border-[#E0E0E0] flex items-center gap-3 sm:gap-4">
                <div>
                  <div className="text-sm font-bold text-[#1A1A1A]">藤井 壮太郎</div>
                  <div className="text-[10px] sm:text-xs text-[#666666]">
                    81合同会社 代表社員 / DXコンサルタント・共創プロデューサー
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Mission & Vision */}
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5 flex flex-col gap-4 sm:gap-6"
          >
            {/* Mission */}
            <div className="bg-[#1A1A1A] p-5 sm:p-7 lg:p-8 flex-1">
              <span
                className="text-[9px] sm:text-[10px] font-bold tracking-widest uppercase text-[#666666] mb-2 sm:mb-3 block"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Mission
              </span>
              <ul className="space-y-1.5 sm:space-y-2">
                {[
                  "人生に価値ある時間の創造",
                  "先進技術を駆使した新しいライフスタイルの創造",
                  "仲間と笑って語る場の創造",
                  "取引先の利益に貢献し社会に貢献する",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs sm:text-sm text-[#CCCCCC]">
                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-[#E03030] mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Vision */}
            <div className="bg-[#E03030] p-5 sm:p-7 lg:p-8">
              <span
                className="text-[9px] sm:text-[10px] font-bold tracking-widest uppercase text-white/60 mb-2 sm:mb-3 block"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Vision
              </span>
              <p className="text-base sm:text-lg font-bold text-white leading-snug">
                「おもろい」にチャレンジし、
                <br />
                社会にインパクトを与える。
              </p>
            </div>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.1 }} className="mb-10 sm:mb-16">
          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-5 sm:mb-8">
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666] mr-2 sm:mr-3"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Values
            </span>
            私たちの行動指針
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {VALUES.map((value, i) => (
              <motion.div
                key={value.title}
                {...fadeInUp}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                className="group p-4 sm:p-6 border border-[#E0E0E0] hover:border-[#E03030] transition-all duration-300 bg-white"
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-[#FEF2F2] text-[#E03030] rounded-sm mb-3 sm:mb-4">
                  <value.icon size={16} strokeWidth={1.5} className="sm:hidden" />
                  <value.icon size={18} strokeWidth={1.5} className="hidden sm:block" />
                </div>
                <h4 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-1.5 sm:mb-2 leading-snug">
                  {value.title}
                </h4>
                <p className="text-[11px] sm:text-xs text-[#333333] leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Company Info Table */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.2 }}>
          <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-4 sm:p-6 lg:p-8">
            <h3 className="text-sm sm:text-base font-bold text-[#1A1A1A] mb-4 sm:mb-6">会社概要</h3>
            <div className="space-y-0">
              {COMPANY_INFO.map((info) => (
                <div
                  key={info.label}
                  className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-6 py-2.5 sm:py-3 border-b border-[#E0E0E0] last:border-b-0"
                >
                  <span className="shrink-0 w-28 text-[10px] sm:text-xs font-bold text-[#666666] uppercase tracking-wider">
                    {info.label}
                  </span>
                  <span className="text-xs sm:text-sm text-[#333333]">{info.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-[#E0E0E0]">
              <a
                href="https://eightone.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-[#E03030] hover:underline"
              >
                公式サイトで詳しく見る
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
